import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { getUserDisplayName } from '@avanta/shared/utils/users';
import { useTranslation } from 'react-i18next';
import { type NavigateOptions, useNavigate } from 'react-router';
import { useShallow } from 'zustand/react/shallow';
import { getRoutes, type NavRoute } from 'constants/nav';
import { ROUTES } from 'constants/routes';
import { AuthService } from 'services/auth';
import { useUserStore } from 'stores/user';

type UseNavActionsOptions = {
  onNavigate?: () => void;
};

export type UseNavActionsResult = {
  greetingLabel: string;
  primaryNavRoutes: NavRoute[];
  primaryNavLinkLabel: (route: NavRoute) => string;
  showScheduleButton: boolean;
  showSupportButton: boolean;
  handleProfile: () => void;
  handleLogout: () => void;
};

export const useNavActions = ({ onNavigate }: UseNavActionsOptions = {}): UseNavActionsResult => {
  const navigate = useNavigate();
  const { t } = useTranslation(LOCIZE_NS.COMMON);
  const { user, isEmployee, isPatient, isPsychologist, isSupporter } = useUserStore(
    useShallow(state => ({
      user: state.user,
      isEmployee: state.isEmployee,
      isPatient: state.isPatient,
      isPsychologist: state.isPsychologist,
      isSupporter: state.isSupporter,
    }))
  );

  const showScheduleButton = (isPatient || isEmployee) && !!user?.canBookAppointments;
  const showSupportButton = !isPsychologist && !isSupporter;
  const greetingLabel = t('Nav.home', { nickname: getUserDisplayName(user) });
  const primaryNavRoutes = getRoutes(user);

  const primaryNavLinkLabel = (route: NavRoute): string => t(`Nav.${route.label}`);

  const navigateTo = (route: string, options?: NavigateOptions): void => {
    navigate(route, options);
    onNavigate?.();
  };

  const handleProfile = (): void => {
    navigateTo(ROUTES.PROFILE);
  };

  const handleLogout = (): void => {
    AuthService.logout();
  };

  return {
    greetingLabel,
    primaryNavRoutes,
    primaryNavLinkLabel,
    showScheduleButton,
    showSupportButton,
    handleProfile,
    handleLogout,
  };
};
